import { Trans } from '@lingui/macro'
import { Box, CircularProgress, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material'
import { useUserPermissions } from '@om1/platform-authentication'
import { PlatformPermissions, RoutedFrameworkComponentProps } from '@om1/platform-utils'
import { ChangeEvent, FunctionComponent, useCallback } from 'react'
import { generalSettingsActions, GeneralSettingsState } from '../state/general-settings'

export type GeneralSettingsPageComponentProps = RoutedFrameworkComponentProps<
    {},
    {},
    {
        generalSettings: GeneralSettingsState
    },
    typeof generalSettingsActions,
    {}
>

export const GeneralSettingsPageComponent: FunctionComponent<GeneralSettingsPageComponentProps> = ({ state, actions }) => {
    const { generalSettings } = state
    const { permissions, decodedToken } = useUserPermissions()

    const canManageOrgMetadata = permissions?.includes(PlatformPermissions.MANAGE_ORGANIZATION_METADATA)
    const hideSystemCohorts = generalSettings.organizationMetadata?.hideSystemCohorts ?? false
    const organizationId = decodedToken?.org_id

    const handleToggleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.checked

            // If org metadata exists, update it
            if (generalSettings.organizationMetadata?.id) {
                actions.updateOrganizationSettings({
                    id: generalSettings.organizationMetadata.id,
                    data: {
                        hideSystemCohorts: newValue
                    }
                })
            } else if (organizationId) {
                // If org metadata doesn't exist, create it
                actions.createOrganizationSettings({
                    data: {
                        organizationId: organizationId,
                        hideSystemCohorts: newValue
                    }
                })
            }
        },
        [actions, generalSettings.organizationMetadata?.id, organizationId]
    )

    if (generalSettings.loading) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' flex='1' minHeight='200px'>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box>
            <Typography variant='h4' sx={{ marginBottom: 3, paddingLeft: '30px', paddingTop: '30px' }}>
                <Trans>General Settings</Trans>
            </Typography>

            <Box
                sx={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    maxWidth: 600,
                    paddingLeft: '30px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant='body1' fontWeight='medium'>
                            <Trans>Hide System Cohorts</Trans>
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 400 }}>
                            <Trans>
                                When enabled, system cohorts will be hidden from the Cohort Dashboard (except for the Real World Data Cloud and
                                Network cohorts).
                            </Trans>
                        </Typography>
                    </Box>

                    <Tooltip title={!canManageOrgMetadata ? <Trans>You do not have permission to manage organization settings</Trans> : ''}>
                        <span>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={hideSystemCohorts}
                                        onChange={handleToggleChange}
                                        disabled={!canManageOrgMetadata || generalSettings.saving || !organizationId}
                                        color='primary'
                                    />
                                }
                                label={hideSystemCohorts ? <Trans>On</Trans> : <Trans>Off</Trans>}
                                labelPlacement='start'
                            />
                        </span>
                    </Tooltip>
                </Box>

                {generalSettings.saving && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant='body2' color='text.secondary'>
                            <Trans>Saving...</Trans>
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
